import { usePricingTiers } from '@/hooks/usePricingTiers'

interface OrganizationData {
  type: 'Organization'
  name: string
  url: string
  logo?: string
  sameAs?: string[]
}

interface SoftwareApplicationData {
  type: 'SoftwareApplication'
  name: string
  description: string
  url: string
  applicationCategory?: string
  operatingSystem?: string
  price?: string
  priceCurrency?: string
}

interface WebPageData {
  type: 'WebPage'
  name: string
  description: string
  url: string
}

interface FAQData {
  type: 'FAQPage'
  questions: { question: string; answer: string }[]
}

interface ProductOffer {
  name: string
  price: string
  priceCurrency?: string
  description?: string
  url?: string
}

interface ProductData {
  type: 'Product'
  name: string
  description: string
  url: string
  brand?: string
  offers: ProductOffer[]
}

type StructuredDataType = OrganizationData | SoftwareApplicationData | WebPageData | FAQData | ProductData

interface StructuredDataProps {
  data: StructuredDataType
}

function generateOrganizationSchema(data: OrganizationData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: data.name,
    url: data.url,
    logo: data.logo,
    sameAs: data.sameAs,
  }
}

function generateSoftwareApplicationSchema(data: SoftwareApplicationData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: data.name,
    applicationCategory: data.applicationCategory || 'BusinessApplication',
    operatingSystem: data.operatingSystem || 'Web',
    description: data.description,
    url: data.url,
    provider: {
      '@type': 'Organization',
      name: 'WealthSpace Ltd',
      url: 'https://www.wealthspace.co.uk',
    },
    ...(data.price && {
      offers: {
        '@type': 'Offer',
        price: data.price,
        priceCurrency: data.priceCurrency || 'GBP',
        priceValidUntil: '2026-12-31',
      },
    }),
  }
}

function generateWebPageSchema(data: WebPageData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: data.name,
    description: data.description,
    url: data.url,
    publisher: {
      '@type': 'Organization',
      name: 'WealthSpace Ltd',
      url: 'https://www.wealthspace.co.uk',
    },
  }
}

function generateFAQSchema(data: FAQData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }
}

function generateProductSchema(data: ProductData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.name,
    description: data.description,
    url: data.url,
    brand: {
      '@type': 'Brand',
      name: data.brand ?? 'WealthSpace',
    },
    offers: data.offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      description: offer.description,
      price: offer.price,
      priceCurrency: offer.priceCurrency ?? 'GBP',
      url: offer.url,
      availability: 'https://schema.org/InStock',
    })),
  }
}

function generateSchema(data: StructuredDataType) {
  switch (data.type) {
    case 'Organization':
      return generateOrganizationSchema(data)
    case 'SoftwareApplication':
      return generateSoftwareApplicationSchema(data)
    case 'WebPage':
      return generateWebPageSchema(data)
    case 'FAQPage':
      return generateFAQSchema(data)
    case 'Product':
      return generateProductSchema(data)
    default:
      return null
  }
}

export default function StructuredData({ data }: StructuredDataProps) {
  const schema = generateSchema(data)

  if (!schema) return null

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}

// Pre-configured structured data for common pages
export function HomePageStructuredData() {
  const { tiers } = usePricingTiers()
  const entryPrice = tiers
    .map((t) => t.monthlyPrice)
    .filter((p): p is number => typeof p === 'number')
    .sort((a, b) => a - b)[0]

  return (
    <StructuredData
      data={{
        type: 'SoftwareApplication',
        name: 'WealthSpace',
        description:
          'AI-powered practice management software for UK financial advisers. Generate suitability reports, manage clients, and support FCA compliance.',
        url: 'https://www.wealthspace.co.uk',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        ...(entryPrice !== undefined && { price: String(entryPrice), priceCurrency: 'GBP' }),
      }}
    />
  )
}

export function OrganizationStructuredData() {
  return (
    <StructuredData
      data={{
        type: 'Organization',
        name: 'WealthSpace Ltd',
        url: 'https://www.wealthspace.co.uk',
        logo: 'https://www.wealthspace.co.uk/og-image.png',
      }}
    />
  )
}
